import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectTraditionalLTCQuotes, selectClient } from '@client/model';
import { getMaritalClassForTraditionalLTC } from '@client/hooks/useShopInsuranceState';
import { useTraditionalLTCQuotesWithROI } from '@client/hooks/useTraditionalLTCQuotesWithROI';
import type { PaymentOption } from '@client/components/ShopInsuranceModal';

const ELIGIBLE_PAYMENT_OPTIONS = ['Annually', 'Monthly'];

interface UseFilteredTraditionalLTCQuotesProps {
  paymentOption: PaymentOption;
  selectedBuyerOption: {
    key: '1-buyer' | '2-buyer';
    label: string;
    maritalClass: string;
  };
  hasPartnerWithDemographics: boolean;
  returnOnInvestmentCalculationType: ReturnOnInvestmentCalculationType;
}

export function useFilteredTraditionalLTCQuotes({
  paymentOption,
  selectedBuyerOption,
  hasPartnerWithDemographics,
  returnOnInvestmentCalculationType,
}: UseFilteredTraditionalLTCQuotesProps) {
  const client = useSelector(selectClient);
  const traditionalLTCQuotes = useSelector(selectTraditionalLTCQuotes);

  const filteredRawTraditionalLTCQuotes = useMemo(() => {
    if (!ELIGIBLE_PAYMENT_OPTIONS.includes(paymentOption)) {
      return [];
    }

    const paymentFrequencyMapping = {
      Annually: 'Annually',
      Monthly: 'Monthly',
    };

    const targetFrequency =
      paymentFrequencyMapping[paymentOption as keyof typeof paymentFrequencyMapping];

    const targetMaritalClass = getMaritalClassForTraditionalLTC(
      paymentOption,
      selectedBuyerOption,
      hasPartnerWithDemographics,
      client?.intakeSurvey?.clientMaritalStatus,
    );

    const filtered = traditionalLTCQuotes.filter(quote => {
      const frequencyMatch = quote.premiumFrequency === targetFrequency;
      const maritalMatch = quote.maritalClass === targetMaritalClass;

      return frequencyMatch && maritalMatch;
    });

    return filtered;
  }, [
    paymentOption,
    selectedBuyerOption,
    client?.intakeSurvey?.clientMaritalStatus,
    traditionalLTCQuotes,
    hasPartnerWithDemographics,
  ]);

  const { quotesWithROI: filteredTraditionalLTCQuotes } = useTraditionalLTCQuotesWithROI({
    quotes: filteredRawTraditionalLTCQuotes,
    client,
    returnOnInvestmentCalculationType,
  });

  return {
    filteredTraditionalLTCQuotes,
  };
}
